import axios, { AxiosResponse } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import License from "../common/models/License";
import Listing from "../common/models/Listing";
import MasterCopy from "../common/models/MasterCopy";
import SongCollection from "../common/models/SongCollections";
import SongToken from "../common/models/SongToken";
import { User, UserDetailResponse } from "../common/models/User";
import Pill from "../components/Buttons/Pill";
import SimpleButton from "../components/Buttons/SimpleButton";
import MasterCopyCard, {
  CollectionCard,
  SongCard,
} from "../components/Card/SongCard";
import Column from "../components/Column/Column";
import RoundedProfilePic from "../components/Images/RoundedProfilePic";
import SearchInput from "../components/Inputs/SearchInput";
import { NavBar } from "../components/NavBar";
import Row from "../components/Row/Row";
import Text from "../components/Text/Text";
import { useAuth } from "../hooks/useAuth";
import default_user_pic from "../imgs/default_user_pic.svg";
import edit_icon from "../imgs/edit_icon.svg";
import light_mode_edit_icon from "../imgs/light_mode_edit_icon.svg";
import light_mode_tick_icon from "../imgs/light_mode_tick_icon.svg";
import tick_icon from "../imgs/tick_icon.png";
import Song from "../songdetail/models/Song";
import { getCutWalletAddress } from "../utils";
import { EditProfileModal } from "./EditProfileModal";

const UserProfilePageContainer = styled.div`
  background: ${(props) =>
    props.theme.themeType === "dark" ? "#000D30" : "white"};
  mix-blend-mode: normal;
  background-size: cover;
  display: flex;
  flex-direction: row;
  background-repeat: no-repeat;
  min-height: 100vh;
  z-index: 1;
  padding-top: 3%;
`;

const UserBackgroundPicture = styled("div")<{ src: string }>`
  position: absolute;
  top: 0;
  left: 0;
  min-width: 100%;
  min-height: 30vh;
  background: url(${(props) => props.src}) no-repeat;
  background-size: cover;
  z-index: 0;
`;

const UserInfoContainer = styled.div`
  position: absolute;
  display: flex;
  top: 150px;
  flex-direction: column;
  align-items: center;
  border-radius: 30px;
  background: ${(props) =>
    props.theme.themeType === "dark" ? "rgba(196, 196, 196, 0.1)" : "white"};
  width: 20%;
  padding: 10px;
  text-align: center;
  height: 65%;
  border: ${(props) =>
    props.theme.themeType === "dark"
      ? "1px solid #ffffff1A"
      : "1px solid #879AE4"};
  z-index: 1;
  backdrop-filter: blur(61.9333px);

  @media screen and (max-width: 650px) {
    top: 0px;
    margin-top: 16px;
    display: none;
  }
`;
const EditIcon = styled.div`
  background: url(${(props) =>
      props.theme.themeType === "dark" ? edit_icon : light_mode_edit_icon})
    no-repeat;
  background-size: contain;
  margin-top: 15px;
  margin-right: 10px;
  width: 5px;
  height: 5pxx;
  outline: none;
  align-self: end;
  padding: 10px;
  &:active {
    transform: scale(0.98);
    box-shadow: 3px 2px 22px 1px rgba(0, 0, 0, 0.24);
  }
`;

const CreateNFTButton = styled(SimpleButton)`
  position: absolute;
  top: 90%;
`;

const NotVerifiedText = styled(Text)`
  position: absolute;
  top: 95%;
  max-width: 15%;
  text-align: center;
`;

const VerificationText = styled(Text)`
  color: #c4c4c4;
`;

const UserProfileColumn = styled(Column)`
  width: 40%;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const MediaSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 27vh;
  z-index: 0;

  @media screen and (max-width: 650px) {
    h2 {
      display: none;
    }

    margin-top: 20vh;
  }
`;

interface UserProfileProps {
  theme: string;
  toggleTheme: any;
}

const SortByRow = styled(Row)`
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const SearchAndFilterContainer = styled.div<{ leftAlign: boolean }>`
  display: flex;
  justify-content: end;
  margin-left: auto;
  width: 30%;
  margin-right: 5%;

  @media screen and (max-width: 650px) {
    width: 100%;
    
  }
`;

const MobileUserProfile = styled.div`
  display: none;

  @media screen and (max-width: 650px) {
    display: flex;
    flex-direction: column;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
  }
`;

const SortByButtonsRow = styled(Row)`
  @media screen and (max-width: 650px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const UserProfilePage: React.FC<UserProfileProps> = ({
  theme,
  toggleTheme,
}) => {
  const auth = useAuth();
  const [user, setUser] = useState<User>({
    first_name: "Foo",
    last_name: "Bar",
    stage_name: "Software developer",
    verified: false,
    profile_pic_s3_link: "",
    background_s3_link: "",
    wallet_address: "",
    description: "",
    email: "",
  });

  const [selectedPill, setSelectedPill] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [ownedSongs, setOwnedSongs] = useState<Array<MasterCopy>>([]);
  const [songTokens, setSongTokens] = useState<Array<Song>>([]);
  const [originalSongTokens, setOriginalSongTokens] = useState<Array<Song>>([]);
  const [licensedSongs, setLicensedSongs] = useState<Array<MasterCopy>>([]);
  const [createdSongs, setCreatedSongs] = useState<Array<MasterCopy>>([]);
  const [masterCopies, setMasterCopies] = useState(new Map());
  const [showMasterCopies, setShowMasterCopies] = useState(true);
  const [newDrops, setNewDrops] = useState<Array<SongCollection>>([]);
  const [released, setReleased] = useState<Array<SongCollection>>([]);
  const [collections, setCollection] = useState<Array<SongCollection>>([]);
  const [originalCollection, setOriginalCollection] = useState<
    Array<SongCollection>
  >([]);
  const [ownedListings, setOwnedListings] = useState<Listing[]>();
  const [licenses, setLicenses] = useState<Array<License>>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { id } = useParams<string>();
  const [isOwnerProfile, setIsOwnerProfile] = useState(false);
  const ref = useRef<any>();
  const navigate = useNavigate();

  const outsideModalClickListener = (event: MouseEvent) => {
    // Do nothing if clicking ref's element or descendent elements
    if (!ref.current || ref.current.contains(event.target)) {
      return;
    }

    setShowModal(false);
  };

  const escapeClose = (e: KeyboardEvent) => {
    // Hitting escape will close the modal
    if (e.key === "Escape") {
      setShowModal(false);
    }
  };

  const refreshUser = () => {
    if (auth && auth.user && id === auth.user.wallet_address) {
      auth.refreshUser((updatedUser: User) => {
        setUser(updatedUser);
      });
    } else {
      axios
        .get<UserDetailResponse>(
          process.env.REACT_APP_BACKEND_URL +
            "/get_user_details?wallet_address=" +
            id
        )
        .then((response: AxiosResponse<UserDetailResponse>) => {
          const user = response.data.userDetails as User;
          setUser(user);
        });
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", escapeClose);
    document.addEventListener("mousedown", outsideModalClickListener);

    return () => {
      window.removeEventListener("keydown", escapeClose);
      document.addEventListener("mousedown", outsideModalClickListener);
    };
  }, [ref]);

  useEffect(() => {
    refreshSongs();
    refreshUser();
    if (
      auth &&
      auth.user &&
      auth.user.wallet_address !== null &&
      auth.user.wallet_address !== ""
    ) {
      setIsOwnerProfile(id === auth.user.wallet_address);
    }
  }, [id]);

  useEffect(() => {
    ownedSongs &&
      ownedSongs.length > 0 &&
      createdSongs &&
      createdSongs.length > 0 &&
      axios
        .post(
          process.env.REACT_APP_BACKEND_URL + "/get_listings",
          JSON.stringify({
            ids: Array.from(masterCopies.values()).map((song) => {
              return song.active_listing_id;
            }),
          }),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          setOwnedListings(Listing.fromResponse(response));
        });
  }, [ownedSongs, createdSongs]);

  const refreshSongs = () => {
    if (auth && auth.user && id === auth.user.wallet_address) {
      // For owner profile
      getOwnedSongs(auth.user.wallet_address);
      getCreatedSongs(auth.user.wallet_address);
      getLicensedSongs(auth.user.wallet_address);
      getLicenses(auth.user.wallet_address);
      getSongCollections(auth.user.wallet_address);
      getOwnedTokens(auth.user.wallet_address);
    } else {
      // For guest profile
      getOwnedSongs(id);
      getCreatedSongs(id);
      getSongCollections(id);
    }
  };

  const getSongCollections = (address: string) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_song_collections?wallet_address=" +
          address
      )
      .then((response: AxiosResponse) => {
        const collections = response.data
          .song_collections as Array<SongCollection>;
        setCollection(collections);
        setNewDrops(
          collections.filter((collection) => {
            return !collection.for_secondary_sale;
          })
        );

        setReleased(
          collections.filter((collection) => {
            return collection.for_secondary_sale;
          })
        );
        setOriginalCollection(collections);
      });
  };

  const getLicenses = (address: string) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_licenses_for_owner?wallet_address=" +
          address
      )
      .then((response: AxiosResponse) => {
        const licenses = response.data.licenses_for_owner as Array<License>;
        setLicenses(licenses);
      });
  };

  const getOwnedTokens = (address: string) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_owned_tokens?wallet_address=" +
          address
      )
      .then((response: AxiosResponse) => {
        const tokens = response.data.owned_tokens as Array<SongToken>;

        getCollectionsFromTokens(tokens);
      });
  };

  const getCollectionsFromTokens = (tokens: Array<SongToken>) => {
    const json = JSON.stringify({ tokens: tokens });
    axios
      .post(
        process.env.REACT_APP_BACKEND_URL + "/get_collections_from_tokens",
        json,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response: AxiosResponse) => {
        const collections = response.data.collections as Array<SongCollection>;
        const songs = tokens.map((token, index) => {
          return Song.fromSongToken(token, collections[index]);
        });
        setSongTokens(songs);
        setOriginalSongTokens(songs);
      });
  };

  const onCreateNFTClick = () => {
    navigate("/create_nft");
  };

  const getOwnedSongs = (id: string) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_owned_songs?wallet_address=" +
          id
      )
      .then((response: AxiosResponse) => {
        const songs = response.data.owned_songs as Array<MasterCopy>;
        setOwnedSongs(songs);
        songs.forEach((item) => masterCopies.set(JSON.stringify(item), item));
      });
  };
  const getLicensedSongs = (wallet_address: string) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_licensed_songs?wallet_address=" +
          wallet_address
      )
      .then((response: AxiosResponse) => {
        const songs = response.data.licensed_songs as Array<MasterCopy>;
        setLicensedSongs(songs);
      });
  };

  const getCreatedSongs = (id: string) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_minted_songs?wallet_address=" +
          id
      )
      .then((response: AxiosResponse) => {
        const songs = response.data.minted_songs as Array<MasterCopy>;
        setCreatedSongs(songs);
        songs.forEach((item) => masterCopies.set(JSON.stringify(item), item));
      });
  };

  const onAllClicked = () => {
    setSelectedPill("All");
    setCollection(originalCollection);
    setSongTokens(originalSongTokens);
    setShowMasterCopies(true);
  };

  const onReleasedClicked = () => {
    setSelectedPill("Released");
    setCollection(released);
    setSongTokens(
      originalSongTokens.filter((songToken) => songToken.for_secondary_sale)
    );
    setShowMasterCopies(false);
  };

  const onNewDropsClicked = () => {
    setSelectedPill("New Drops");
    setCollection(newDrops);
    setSongTokens(
      originalSongTokens.filter((songToken) => !songToken.for_secondary_sale)
    );
    setShowMasterCopies(false);
  };
  const onEditIconClick = () => {
    setShowModal(true);
  };

  const toggleShow = (show: boolean) => {
    setShowModal(show);
    if (auth && auth.user) {
      auth.refreshUser((updatedUser: User) => {
        setUser(updatedUser);
      });
    }
  };

  return (
    <>
      <NavBar theme={theme} themeToggler={toggleTheme} />
      <UserProfilePageContainer>
        <UserBackgroundPicture src={user.background_s3_link} />
        {isOwnerProfile && showModal && (
          <EditProfileModal
            theme={theme}
            user={user}
            ref={ref}
            toggleShow={toggleShow}
            show={showModal}
          />
        )}

        <UserProfileColumn>
          <UserInfoContainer>
            {isOwnerProfile && <EditIcon onClick={onEditIconClick} />}

            <RoundedProfilePic
              bg={
                user.profile_pic_s3_link === null
                  ? default_user_pic
                  : user.profile_pic_s3_link
              }
              outline={false}
            />
            <Row>
              <h3>
                {user.stage_name === null
                  ? getCutWalletAddress(user.wallet_address)
                  : user.stage_name}
              </h3>
              {user.verified && (
                <img
                  src={theme === "dark" ? tick_icon : light_mode_tick_icon}
                  style={{ width: "17px", marginLeft: "5px" }}
                />
              )}
            </Row>
            {/* TODO: Uncomment this line for public launch*/}
            {!user.verified && user.stage_name !== null && (
              <VerificationText>Verification in progress</VerificationText>
            )}
            <VerificationText>
              {getCutWalletAddress(user.wallet_address)}
            </VerificationText>
            <Text>{user.description}</Text>
          </UserInfoContainer>
          {isOwnerProfile && (
            <>
              {user.verified || user.stage_name === null ? (
                <CreateNFTButton bg="primary" onClick={onCreateNFTClick}>
                  Create NFT
                </CreateNFTButton>
              ) : (
                <>
                  <CreateNFTButton disabled onClick={onCreateNFTClick}>
                    Create NFT
                  </CreateNFTButton>
                  <NotVerifiedText size={"14px"}>
                    Your account is currently under review, once approved, you
                    will be able to create NFTs!
                  </NotVerifiedText>
                </>
              )}
            </>
          )}
        </UserProfileColumn>

        <MediaSection>
          <MobileUserProfile>
            <RoundedProfilePic
              bg={
                user.profile_pic_s3_link === null
                  ? default_user_pic
                  : user.profile_pic_s3_link
              }
              outline={false}
            />

            <Text>{user.description}</Text>
          </MobileUserProfile>

          <SortByRow>
            {isOwnerProfile && (
              <SortByButtonsRow gap={"1rem"}>
                <Text>Sort by</Text>
                <Pill active={selectedPill === "All"} onClick={onAllClicked}>
                  All
                </Pill>
                <Pill
                  active={selectedPill === "Released"}
                  onClick={onReleasedClicked}
                >
                  Released
                </Pill>
                <Pill
                  active={selectedPill === "New Drops"}
                  onClick={onNewDropsClicked}
                >
                  New Drops
                </Pill>
                <Pill
                  active={selectedPill === "Master Copies"}
                  onClick={() => {
                    setSelectedPill("Master Copies");
                    setShowMasterCopies(true);
                    setCollection([]);
                    setSongTokens([]);
                  }}
                >
                  Master Copies
                </Pill>
                <Pill
                  active={selectedPill === "Licenses"}
                  onClick={() => {
                    setSelectedPill("Licenses");
                    setShowMasterCopies(true);
                    setCollection([]);
                    setSongTokens([]);
                  }}
                >
                  Licenses
                </Pill>
              </SortByButtonsRow>
            )}
            <SearchAndFilterContainer leftAlign={!isOwnerProfile}>
              <SearchInput
                onTextChange={(text: string) => {
                  setSearchQuery(text);
                }}
                theme={theme}
              />
            </SearchAndFilterContainer>
          </SortByRow>
          <GridLayout
            masterCopies={
              showMasterCopies
                ? selectedPill == "Licenses"
                  ? Array.from(masterCopies.values()).filter((masterCopy) => {
                      return masterCopy.license_address !== null;
                    })
                  : Array.from(masterCopies.values())
                : []
            }
            licensedSongs={licensedSongs.filter((song) => {
              return !masterCopies.has(JSON.stringify(song));
            })}
            licenses={licenses}
            searchQuery={searchQuery}
            isOwner={isOwnerProfile}
            listings={ownedListings}
            collections={collections}
            songs={songTokens}
          />
        </MediaSection>

        <Outlet />
      </UserProfilePageContainer>
    </>
  );
};

interface GridLayoutProps {
  masterCopies: Array<MasterCopy>;
  licensedSongs: Array<MasterCopy>;
  licenses: Array<License>;
  searchQuery: string;
  isOwner: boolean;
  listings: Listing[];
  collections?: SongCollection[];
  songs?: Song[];
}

interface GridItemProps {
  masterCopy: MasterCopy;
  licensedSong?: boolean;
  revokedSong?: boolean;
  onSongClick: (song: MasterCopy) => void;
}

const CustomRow = styled("div")`
  display: flex;
  gap: 3%;
  flex-wrap: wrap;

  @media screen and (max-width: 650px) {
    padding: 4%;
    gap: 25px;
  }
`;

const GridLayout: React.FC<GridLayoutProps> = ({
  masterCopies,
  searchQuery,
  licensedSongs,
  licenses,
  isOwner,
  listings,
  collections,
  songs,
}) => {
  const navigate = useNavigate();

  const GridItem = ({
    masterCopy,
    onSongClick,
    licensedSong,
    revokedSong,
  }: GridItemProps) => {
    return (
      <MasterCopyCard
        song={masterCopy}
        onSongClick={onSongClick}
        showLicensedBanner={licensedSong ? licensedSong && !revokedSong : false}
        showRevokedBanner={revokedSong}
        isOwner={isOwner}
        width={"30%"}
        listing={
          listings &&
          listings.filter((listing) => {
            return listing.master_copy_id === masterCopy.id;
          })[0]
        }
      />
    );
  };

  const onSongClick = (song: MasterCopy) => {
    navigate(`/song/${song.id}`);
  };
  const search = (songs: Array<MasterCopy>) => {
    return songs.filter(
      (song) =>
        song.song_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        song.artist_stage_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
    );
  };

  const searchCollections = (collections: Array<SongCollection>) => {
    return collections.filter(
      (collection) =>
        collection.song_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        collection.artist_stage_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
    );
  };

  const searchSongs = (songs: Array<Song>) => {
    return songs.filter(
      (song) =>
        song.song_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        song.artist_stage_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
    );
  };

  return (
    <>
      <CustomRow>
        {searchCollections(collections).map((collection, index) => (
          <CollectionCard
            key={index}
            collection={collection}
            onCollectionClick={() => {
              navigate(`/collection/${collection.id}`);
            }}
            width={"30%"}
          />
        ))}

        {searchSongs(songs).map((song, index) => (
          <SongCard
            key={index}
            song={song}
            onSongClicked={() => {
              navigate(`/collection/${song.collectionId}`);
            }}
            width={"30%"}
          />
        ))}

        {search(masterCopies).map((masterCopy, index) => (
          <GridItem
            masterCopy={masterCopy}
            key={index}
            onSongClick={onSongClick}
          />
        ))}
        {search(licensedSongs).map((song, index) => (
          <GridItem
            masterCopy={song}
            key={index}
            onSongClick={onSongClick}
            licensedSong={true}
            revokedSong={
              licenses.filter((license) => {
                return license.song_id === song.id;
              }).length > 0
                ? licenses.filter((license) => {
                    return license.song_id === song.id;
                  })[0].revoked
                : false
            }
          />
        ))}
      </CustomRow>
    </>
  );
};
